
import { Box, Typography, Button, Link } from "@mui/material";
import {
  Instagram,
  LocationOnOutlined,
  WhatsApp,
  EmailOutlined
} from "@mui/icons-material";
import PhoneEnabledOutlined from "@mui/icons-material/PhoneEnabledOutlined";
import {AppConfigFields, ContactInfoFields} from "../types/appConfigEntities";
import { useTheme } from "../util/themes";

interface FooterProps {
  companyName: string;
  year?: number;
}

export default function Footer({
  companyName,
  year = new Date().getFullYear()
}: FooterProps) {
  const theme = useTheme();
  
  const commonLinkStyles = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    color: theme.colors.text.primary,
    textDecoration: 'none',
    transition: 'color 0.2s ease-in-out',
    '&:hover': {
      color: theme.colors.text.secondary,
    },
  };
  
    const contactInfo = window.APP_CONFIG[AppConfigFields.ContactData]
    const whatsappMessage = contactInfo?.[ContactInfoFields.WhatsappMessage] || "Hola como estas? Quiero solicitar información."

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.colors.background.paper,
        py: 6,
        px: 2
      }}
    >
      {/* Main Footer Content - Centered */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        



        {/* Navigation Links */}
        <Box sx={{ mb: 4 }}>
          <Button
            component={Link}
            href="/"
            sx={{
              ...commonLinkStyles,
              mx: 1,
              '&:hover': {
                ...commonLinkStyles['&:hover'],
                backgroundColor: 'transparent',
              },
            }}
          >
            inicio
          </Button>
          <Button
            component={Link}
            href="/productos"
            sx={{
              ...commonLinkStyles,
              mx: 1,
              '&:hover': {
                ...commonLinkStyles['&:hover'],
                backgroundColor: 'transparent',
              },
            }}
          >
            productos
          </Button>
        </Box>

        {/* Contact Information */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>

            {/* Instagram Icon */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                <Link
                    href={`https://instagram.com/${contactInfo?.[ContactInfoFields.Instagram] || "mouchi.oficial"}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2, // mas separado como WhatsApp/Telefono
                        textDecoration: 'none',
                        color: theme.colors.text.primary,
                        transition: 'color 0.3s ease-in-out',
                        '&:hover': {
                            color: theme.colors.text.secondary, // hover unificado
                        }
                    }}
                >
                    <Instagram
                        sx={{
                            fontSize: 22,
                            color: 'inherit' // hereda el color del Link
                        }}
                    />

                    <Typography
                        sx={{
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '14px',
                            color: 'inherit', // hereda el color del Link
                        }}
                    >
                        @{contactInfo?.[ContactInfoFields.Instagram] || "mouchi.oficial"}
                    </Typography>
                </Link>
            </Box>



            {/* WhatsApp */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                <Link
                    href={`https://wa.me/${contactInfo?.[ContactInfoFields.Whatsapp]?.replace(/\D/g, '')}?text=${encodeURIComponent(whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        textDecoration: 'none',
                        color: theme.colors.text.primary,
                        transition: 'color 0.3s ease-in-out',
                        '&:hover': { color: theme.colors.text.secondary }, // hover unificado
                    }}
                >
                    <WhatsApp sx={{ fontSize: 18, color: 'inherit' }} />

                    <Typography
                        sx={{
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '14px',
                            color: 'inherit',
                        }}
                    >
                        {contactInfo.whatsapp}
                    </Typography>
                </Link>
            </Box>


            {/* Phone */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <PhoneEnabledOutlined sx={{ color: theme.colors.text.primary, fontSize: 18 }} />
                <Typography
                    sx={{
                        fontFamily: 'Rubik, sans-serif',
                        fontSize: '14px',
                        color: theme.colors.text.primary
                    }}
                >
                    {contactInfo[ContactInfoFields.Phone]}
                </Typography>
            </Box>


            {/* Email */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <EmailOutlined sx={{ color: theme.colors.text.primary, fontSize: 18 }} />
                <Typography
                    sx={{
                        fontFamily: 'Rubik, sans-serif',
                        fontSize: '14px',
                        color: theme.colors.text.primary
                    }}
                >
                    {contactInfo[ContactInfoFields.Email]}
                </Typography>
            </Box>


          {/* Location */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <LocationOnOutlined sx={{ color: theme.colors.text.primary, fontSize: 18 }} />
            <Typography variant="body1" sx={{ ...commonLinkStyles, '&:hover': { color: theme.colors.text.primary } }}>
              {contactInfo[ContactInfoFields.Address]}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Copyright Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: 1,
          borderColor: theme.colors.greyLight,
          pt: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1
        }}
      >
        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>
            © Copyright {companyName} - {year}      |      Powered by ItapTec
        </Typography>

        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>
          Todos los derechos reservados.
        </Typography>
      </Box>
    </Box>
  );
}














